// Deciding what org keys can be handed out right now, and why not where they can't.
//
// Background (§7): a grant is authorization only. Reading a vault additionally needs its org_key
// wrapped onto the user's public key (`org_members`), and only a browser that already holds that
// key can produce the wrap — the server never sees it, and it cannot be wrapped to a *group* at
// all (HPKE targets one public key). So assigning a role to a group leaves its members authorized
// but blind: the vault list comes from `org_members`, so they see nothing.
//
// The backend reports every such pair at GET /admin/access/gaps; this module turns that report
// into a work list for the admin's browser. Pure and import-free so the node harness
// (access-plan.test.mjs) can compile and exercise this exact file.

/** One authorized-but-keyless pair, as /admin/access/gaps serves it. */
export interface AccessGapLike {
  org_id: string;
  org_name: string;
  user_id: string;
  login: string;
  /** The user has a public key, so a wrap can be produced for them. */
  enrolled: boolean;
  /** base64 wrap target; absent when not enrolled. */
  public_key?: string;
  /** Where the authorization comes from: "instance", "org", "group:TopTech". */
  via: string;
}

export type DistStatus =
  /** Can be fixed right now in this browser. */
  | "ready"
  /** The user has never set a master password, so there is no public key to wrap to. */
  | "not-enrolled"
  /** This admin is not a key holder of that vault, so they cannot unwrap it to re-wrap it. */
  | "no-admin-key";

export interface DistRow {
  orgId: string;
  orgName: string;
  userId: string;
  login: string;
  via: string;
  status: DistStatus;
  /** base64 public key — present exactly when status is "ready". */
  publicKey: string;
}

/** Why a row cannot be acted on. Kept next to the statuses so the copy has one home. */
export const STATUS_REASON: Record<DistStatus, string> = {
  ready: "Key will be wrapped to this user",
  "not-enrolled": "User has never unlocked the vault — no public key yet. They must sign in and set a master password first; this row stays here until then.",
  "no-admin-key": "You do not hold this vault's key, so you cannot pass it on. Needs an admin who is a member, or the vault's recovery key.",
};

/**
 * Turn the gap report into per-row decisions.
 *
 * `adminOrgIds` are the vaults the acting admin holds a key for (from GET /orgs); `userIds`
 * optionally narrows the result to the people a just-created grant affected. Rows come back
 * sorted by vault then login so the table is stable across reloads.
 */
export function planDistribution(args: {
  gaps: AccessGapLike[];
  adminOrgIds: string[];
  userIds?: string[];
}): DistRow[] {
  const mine = new Set(args.adminOrgIds);
  const only = args.userIds ? new Set(args.userIds) : null;
  const rows: DistRow[] = [];
  for (const g of args.gaps) {
    if (only && !only.has(g.user_id)) continue;
    // Not-enrolled wins over no-admin-key: nothing can be done for that person anywhere yet,
    // and saying so is more useful than blaming the wrong axis.
    let status: DistStatus = "ready";
    if (!g.enrolled || !g.public_key) status = "not-enrolled";
    else if (!mine.has(g.org_id)) status = "no-admin-key";
    rows.push({
      orgId: g.org_id,
      orgName: g.org_name,
      userId: g.user_id,
      login: g.login,
      via: g.via,
      status,
      publicKey: status === "ready" ? (g.public_key ?? "") : "",
    });
  }
  rows.sort((a, b) => a.orgName.localeCompare(b.orgName) || a.login.localeCompare(b.login));
  return rows;
}

export interface DistSummary {
  total: number;
  ready: number;
  blocked: number;
  byStatus: Record<DistStatus, number>;
  /** Distinct people affected — what the admin actually wants to read off a banner. */
  users: number;
}

/** Counts for the banner and the result line. */
export function summarize(rows: DistRow[]): DistSummary {
  const byStatus: Record<DistStatus, number> = { ready: 0, "not-enrolled": 0, "no-admin-key": 0 };
  const users = new Set<string>();
  for (const r of rows) {
    byStatus[r.status]++;
    users.add(r.userId);
  }
  return {
    total: rows.length,
    ready: byStatus.ready,
    blocked: rows.length - byStatus.ready,
    byStatus,
    users: users.size,
  };
}
